import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { buildTownGroups, TownEntry } from "@/hooks/use-town";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const BOROUGHS = ["North Ayrshire", "East Ayrshire", "South Ayrshire"] as const;
type Borough = typeof BOROUGHS[number];

const BOROUGH_COLOURS: Record<Borough, { bg: string; border: string; text: string; dot: string }> = {
  "North Ayrshire": { bg: "bg-blue-50 dark:bg-blue-900/20",   border: "border-blue-200",  text: "text-blue-800 dark:text-blue-300",  dot: "bg-blue-500"  },
  "East Ayrshire":  { bg: "bg-amber-50 dark:bg-amber-900/20",  border: "border-amber-200", text: "text-amber-800 dark:text-amber-300", dot: "bg-amber-500" },
  "South Ayrshire": { bg: "bg-green-50",  border: "border-green-200", text: "text-green-800", dot: "bg-green-500" },
};

const EMPTY_ADD = { name: "", lat: "", lon: "" };

export default function AdminTowns() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const [towns, setTowns] = useState<TownEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [addForms, setAddForms] = useState<Record<Borough, typeof EMPTY_ADD>>({
    "North Ayrshire": { ...EMPTY_ADD },
    "East Ayrshire":  { ...EMPTY_ADD },
    "South Ayrshire": { ...EMPTY_ADD },
  });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/towns");
      const data = await res.json();
      setTowns(Array.isArray(data) ? data : []);
    } catch {
      toast({ title: "Failed to load towns", variant: "destructive", action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction> });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const persist = async (list: TownEntry[]) => {
    setSaving(true);
    try {
      const res = await fetch("/api/admin/towns", {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(list),
      });
      if (!res.ok) throw new Error("Failed");
      setTowns(list);
      toast({ title: "Towns saved" });
    } catch {
      toast({ title: "Failed to save towns", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (borough: Borough, e: React.FormEvent) => {
    e.preventDefault();
    const { name, lat, lon } = addForms[borough];
    const trimmed = name.trim();
    if (!trimmed) { toast({ title: "Town name is required", variant: "destructive" }); return; }
    if (towns.some(t => t.name.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: "Town already exists", variant: "destructive" }); return;
    }
    const next = [...towns, {
      name: trimmed,
      lat: parseFloat(lat) || 0,
      lon: parseFloat(lon) || 0,
      borough,
    }];
    setAddForms(f => ({ ...f, [borough]: { ...EMPTY_ADD } }));
    await persist(next);
  };

  const handleRemove = async (name: string) => {
    if (!window.confirm(`Remove "${name}" from the town list?`)) return;
    await persist(towns.filter(t => t.name !== name));
  };

  const groups = buildTownGroups(towns.length > 0 ? towns : []);

  // Ensure all 3 boroughs appear even if empty
  const allGroups = BOROUGHS.map(b => ({
    borough: b,
    towns: groups.find(g => g.borough === b)?.towns ?? [],
  }));

  return (
    <AdminLayout current="/admin/towns">
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold text-primary">Manage Towns</h1>
          <p className="text-sm text-slate-500 mt-1">
            Towns are grouped by council area. Lat/lon is used for GPS nearest-town detection.
            Find coordinates at{" "}
            <a href="https://gridreferencefinder.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">
              gridreferencefinder.com
            </a>.
          </p>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm py-8">Loading…</div>
        ) : (
          <div className="space-y-4">
            {allGroups.map(({ borough, towns: groupTowns }) => {
              const colours = BOROUGH_COLOURS[borough as Borough];
              const isCollapsed = collapsed[borough];
              return (
                <div key={borough} className={`rounded-xl border ${colours.border} overflow-hidden`}>
                  {/* Borough header */}
                  <button
                    onClick={() => setCollapsed(c => ({ ...c, [borough]: !c[borough] }))}
                    className={`w-full flex items-center gap-3 px-5 py-3.5 ${colours.bg} transition-colors hover:brightness-95`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colours.dot}`} />
                    <span className={`font-bold text-sm flex-1 text-left ${colours.text}`}>{borough}</span>
                    <span className={`text-xs font-semibold ${colours.text} opacity-70 mr-2`}>
                      {groupTowns.length} {groupTowns.length === 1 ? "town" : "towns"}
                    </span>
                    {isCollapsed
                      ? <ChevronRight size={15} className={colours.text} />
                      : <ChevronDown size={15} className={colours.text} />
                    }
                  </button>

                  {!isCollapsed && (
                    <>
                      {/* Towns list */}
                      {groupTowns.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {groupTowns.map(town => (
                            <div key={town.name} className="flex items-center gap-3 px-5 py-2.5 bg-white group hover:bg-slate-50 transition-colors">
                              <span className="font-medium text-slate-800 text-sm w-36 flex-shrink-0">{town.name}</span>
                              <div className="flex items-center gap-1.5 flex-1">
                                <span className="text-xs text-slate-400 font-mono">{town.lat.toFixed(4)},</span>
                                <span className="text-xs text-slate-400 font-mono">{town.lon.toFixed(4)}</span>
                              </div>
                              <button
                                onClick={() => void handleRemove(town.name)}
                                disabled={saving}
                                className="p-1.5 rounded-lg text-red-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-30 flex-shrink-0"
                                title={`Remove ${town.name}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-5 py-4 bg-white text-sm text-slate-400 italic">
                          No towns in {borough} yet — add one below.
                        </div>
                      )}

                      {/* Add form for this borough */}
                      <form
                        onSubmit={e => void handleAdd(borough as Borough, e)}
                        className="flex flex-wrap gap-2 items-end px-5 py-3.5 bg-slate-50 border-t border-slate-100"
                      >
                        <div className="flex-1 min-w-32">
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Town name *</label>
                          <input
                            value={addForms[borough as Borough].name}
                            onChange={e => setAddForms(f => ({ ...f, [borough]: { ...f[borough as Borough], name: e.target.value } }))}
                            placeholder="e.g. Galston"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-primary/50 bg-white"
                          />
                        </div>
                        <div className="w-24">
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Latitude</label>
                          <input
                            type="number" step="0.0001"
                            value={addForms[borough as Borough].lat}
                            onChange={e => setAddForms(f => ({ ...f, [borough]: { ...f[borough as Borough], lat: e.target.value } }))}
                            placeholder="55.xxxx"
                            className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-primary/50 bg-white"
                          />
                        </div>
                        <div className="w-24">
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Longitude</label>
                          <input
                            type="number" step="0.0001"
                            value={addForms[borough as Borough].lon}
                            onChange={e => setAddForms(f => ({ ...f, [borough]: { ...f[borough as Borough], lon: e.target.value } }))}
                            placeholder="-4.xxxx"
                            className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-primary/50 bg-white"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={saving || !addForms[borough as Borough].name.trim()}
                          className={`flex items-center gap-1.5 font-bold px-3.5 py-2 rounded-lg text-sm transition-colors disabled:opacity-40 ${colours.bg} ${colours.text} border ${colours.border} hover:brightness-95`}
                        >
                          <Plus size={14} /> Add
                        </button>
                      </form>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className={`rounded-xl border border-slate-100 bg-slate-50 px-5 py-3 text-xs text-slate-500 flex items-start gap-2`}>
          <span className="text-base leading-none mt-0.5">💡</span>
          <span>
            Towns without lat/lon (0,0) will still appear in dropdowns but won't be matched by GPS detection.
            You can safely add a town name now and fill in coordinates later.
          </span>
        </div>
      </div>
    </AdminLayout>
  );
}
